import { jsPDF } from 'jspdf';
import 'jspdf-autotable/dist/jspdf.plugin.autotable.min.js';
import html2canvas from 'html2canvas';

export const generateQuickScanPDF = async (scanData, statsData) => {
  const doc = new jsPDF();
  
  // Ensure autoTable is available
  if (!doc.autoTable) {
    console.error('jsPDF autoTable plugin not loaded');
    return doc;
  }
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 25;
  const contentWidth = pageWidth - (margin * 2);
  
  // Color scheme matching reference PDF exactly
  const colors = {
    primary: [99, 102, 241],      // Purple/Indigo 
    secondary: [34, 197, 94],     // Green
    warning: [251, 146, 60],      // Orange
    danger: [239, 68, 68],        // Red
    info: [59, 130, 246],         // Blue
    gray: [156, 163, 175],        // Medium gray
    lightGray: [249, 250, 251],   // Very light gray
    darkGray: [55, 65, 81],       // Dark gray text
    borderGray: [229, 231, 235],  // Light border
    white: [255, 255, 255],
    purple: [139, 92, 246]        // Purple for header
  };
  
  let yPosition = 25;
  
  // HEADER SECTION - Clean and modern design
  // Draw target/bullseye logo
  doc.setDrawColor(...colors.danger);
  doc.setFillColor(...colors.white);
  doc.setLineWidth(2);
  doc.circle(margin, yPosition, 4);
  doc.setFillColor(...colors.danger);
  doc.circle(margin, yPosition, 2, 'F');
  
  // Main title
  doc.setFontSize(26);
  doc.setTextColor(...colors.purple);
  doc.setFont(undefined, 'bold');
  doc.text('GridRank', margin + 12, yPosition + 2);
  
  yPosition += 15;
  doc.setFontSize(18);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...colors.darkGray);
  doc.text('Quick Scan Performance Report', margin, yPosition);
  
  yPosition += 10;
  doc.setFontSize(12);
  doc.setTextColor(...colors.gray);
  doc.text('Comprehensive Analysis of Your Local Search Performance', margin, yPosition);
  
  // Business info section with subtle purple underline
  yPosition += 15;
  doc.setDrawColor(...colors.purple);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  
  yPosition += 10;
  const businessName = scanData.business_name || 'Business Name';
  const reportDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Business: ', margin, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(businessName, margin + 22, yPosition);
  
  doc.setFont(undefined, 'bold');
  doc.text('Generated: ', pageWidth - margin - 65, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(reportDate, pageWidth - margin - 40, yPosition);
  
  // EXECUTIVE SUMMARY with thin green left border
  yPosition += 20;
  
  // Thin green accent bar (like reference)
  doc.setFillColor(...colors.secondary);
  doc.rect(margin, yPosition - 5, .5, 35, 'F');  // Thinner left border
  
  // Bar chart icon
  doc.setFillColor(...colors.gray);
  doc.rect(margin + 8, yPosition, 2, 4, 'F');
  doc.rect(margin + 11, yPosition - 2, 2, 6, 'F');
  doc.rect(margin + 14, yPosition - 1, 2, 5, 'F');
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Executive Summary', margin + 20, yPosition + 2);
  
  yPosition += 12;
  doc.setFontSize(10.5);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...colors.darkGray);
  
  const totalKeywords = (statsData && statsData.total_keywords) || (scanData.keywords && scanData.keywords.split(',').length) || 0;
  const totalLocations = (statsData && statsData.total_locations) || 0;
  const avgRank = (statsData && statsData.average_rank) || 0;
  const top3Percentage = (statsData && statsData.top_3_percentage) || 0;
  const coverage = (statsData && statsData.coverage_percentage) || 0;
  
  doc.text(`Overall Performance: Your local search presence shows ${top3Percentage.toFixed(0)}% of keywords ranking in top 3`, margin + 8, yPosition);
  yPosition += 5;
  doc.text(`positions across ${totalLocations} monitored grid locations. Average ranking position is ${avgRank.toFixed(1)}.`, margin + 8, yPosition);
  yPosition += 8;
  doc.text(`Key Highlights: Grid coverage reached ${coverage.toFixed(0)}% visibility across all tracked locations.`, margin + 8, yPosition);
  
  // KEY METRICS CARDS - 2x2 layout matching reference
  yPosition += 25;
  const cardWidth = (contentWidth - 15) / 2;  // 2 cards per row
  const cardHeight = 60;
  const cardSpacing = 15;
  const verticalSpacing = 15;
  
  const metricsCards = [
    {
      value: totalKeywords.toString(),
      label: 'KEYWORDS TRACKED',
      // sublabel: '+3 new',
      sublabelColor: colors.secondary
    },
    {
      value: avgRank.toFixed(1),
      label: 'AVERAGE POSITION',
      // sublabel: '+1.2 improvement',
      sublabelColor: colors.secondary
    },
    {
      value: `${top3Percentage.toFixed(0)}%`,
      label: 'TOP 3 POSITIONS',
      // sublabel: '+15% increase',
      sublabelColor: colors.secondary
    },
    {
      value: `${coverage.toFixed(0)}%`,
      label: 'GRID COVERAGE',
      // sublabel: '-2% change',
      sublabelColor: colors.danger
    }
  ];
  
  metricsCards.forEach((card, index) => {
    const row = Math.floor(index / 2);
    const col = index % 2;
    const xPos = margin + (col * (cardWidth + cardSpacing));
    const yPos = yPosition + (row * (cardHeight + verticalSpacing));
    
    // White card with very thin border (matching reference)
    doc.setFillColor(...colors.white);
    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);  // Very thin border like reference
    doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3);
    
    // Large value
    doc.setFontSize(32);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.purple);
    doc.text(card.value, xPos + cardWidth/2, yPos + 28, { align: 'center' });
    
    // Label
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text(card.label, xPos + cardWidth/2, yPos + 40, { align: 'center' });
    
    // Sublabel with color
    // doc.setFontSize(10);
    // doc.setTextColor(...card.sublabelColor);
    // doc.text(card.sublabel, xPos + cardWidth/2, yPos + 50, { align: 'center' });
  });
  
  // POSITION PERFORMANCE BREAKDOWN - Matching reference cards exactly
  yPosition += 135;  // Adjusted for 2x2 layout
  
  // Check if we need a new page for Position Performance section
  if (yPosition + 150 > pageHeight - margin) {
    doc.addPage();
    yPosition = margin;
  }
  
  // Bar chart icon
  doc.setFillColor(...colors.gray);
  doc.rect(margin, yPosition, 1.5, 5, 'F');
  doc.rect(margin + 2.5, yPosition - 2, 1.5, 7, 'F');
  doc.rect(margin + 5, yPosition - 1, 1.5, 6, 'F');
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Position Performance Breakdown', margin + 10, yPosition + 2);
  
  yPosition += 20;  // Space after heading
  
  // Position cards with colored borders - 2 per row layout
  const positionCards = [
    {
      value: (statsData && statsData.top_3_count) || 0,
      label: 'POSITIONS 1-3',
      borderColor: colors.secondary
    },
    {
      value: ((statsData && statsData.top_10_count) || 0) - ((statsData && statsData.top_3_count) || 0),
      label: 'POSITIONS 4-7',
      borderColor: colors.purple
    },
    {
      value: ((statsData && statsData.top_20_count) || 0) - ((statsData && statsData.top_10_count) || 0),
      label: 'POSITIONS 8-15',
      borderColor: colors.warning
    },
    {
      value: ((statsData && statsData.ranked_count) || 0) - ((statsData && statsData.top_20_count) || 0),
      label: 'POSITION 16+',
      borderColor: colors.danger
    },
    {
      value: (statsData && statsData.not_ranked_count) || 0,
      label: 'NOT RANKED',
      borderColor: [200, 200, 200]
    }
  ];
  
  // 2 cards per row, last one full width if alone
  const defaultCardWidth = (contentWidth - 15) / 2;
  const fullCardWidth = contentWidth;
  const posCardHeight = 55;
  const posCardSpacing = 15;
  const posVerticalSpacing = 15;
  
  positionCards.forEach((card, index) => {
    let xPos, yPos, cardWidth;
    
    const row = Math.floor(index / 2);
    const col = index % 2;
    const isLastCard = index === positionCards.length - 1;
    const isAloneInRow = isLastCard && (positionCards.length % 2 === 1);
    
    if (isAloneInRow) {
      // Last card alone - full width
      cardWidth = fullCardWidth;
      xPos = margin;
      yPos = yPosition + (row * (posCardHeight + posVerticalSpacing));
    } else {
      // Regular 2 per row layout
      cardWidth = defaultCardWidth;
      xPos = margin + (col * (cardWidth + posCardSpacing));
      yPos = yPosition + (row * (posCardHeight + posVerticalSpacing));
    }
    
    // White background
    doc.setFillColor(...colors.white);
    doc.rect(xPos, yPos, cardWidth, posCardHeight, 'F');
    
    // Colored border - very thin like reference
    doc.setDrawColor(...card.borderColor);
    doc.setLineWidth(0.5);  // Very thin border
    doc.roundedRect(xPos, yPos, cardWidth, posCardHeight, 3, 3);
    
    // Value
    doc.setFontSize(28);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...card.borderColor);
    doc.text(card.value.toString(), xPos + cardWidth/2, yPos + 28, { align: 'center' });
    
    // Label
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text(card.label, xPos + cardWidth/2, yPos + 42, { align: 'center' });
  });
  
  // Update yPosition to account for the actual number of rows (3 rows for 5 cards with 2 per row)
  const totalRows = Math.ceil(positionCards.length / 2);
  yPosition += (posCardHeight * totalRows) + (posVerticalSpacing * (totalRows - 1)) + 20;
  
  // GEOGRAPHIC GRID PERFORMANCE MAP - Start on new page
  doc.addPage();
  yPosition = margin;
  
  // Add section title on new page
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Geographic Grid Performance Map', pageWidth/2, yPosition, { align: 'center' });
  
  yPosition += 15;
  
  // Use the pre-captured map image if available (includes header and map)
  if (scanData.mapImage) {
    try {
      // Calculate dimensions to fit the page properly
      const maxWidth = pageWidth - (margin * 2);
      const maxHeight = pageHeight - yPosition - margin;
      
      // The image includes both header and map, so we need more space
      // Use most of the page for the map image
      const imgWidth = maxWidth + 5;
      const imgHeight = Math.min(maxHeight, 100); // Larger height for full capture
      
      doc.addImage(scanData.mapImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 15;
      
    } catch (error) {
      console.log('Could not add map image, using grid visualization');
      // Fallback: Add legend and grid visualization
      addLegendAndGrid();
    }
  } else {
    // No map image provided, use fallback
    addLegendAndGrid();
  }
  
  function addLegendAndGrid() {
    // Map pin icon
    doc.setFillColor(...colors.danger);
    doc.setDrawColor(...colors.danger);
    const pinX = margin + 1;
    const pinY = yPosition;
    doc.circle(pinX, pinY - 1, 2, 'F');
    doc.setLineWidth(0);
    doc.triangle(pinX - 2, pinY - 1, pinX + 2, pinY - 1, pinX, pinY + 3, 'F');
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text('Geographic Performance', margin + 10, yPosition + 1);
    
    // Legend
    yPosition += 15;
    const legendItems = [
      { color: colors.secondary, label: 'Positions 1-3 (Excellent)' },
      { color: colors.purple, label: 'Positions 4-7 (Good)' },
      { color: colors.warning, label: 'Positions 8-15 (Fair)' },
      { color: colors.danger, label: 'Position 16+ (Poor)' },
      { color: [200, 200, 200], label: 'Not Ranked' }
    ];
    
    doc.setFontSize(9);
    legendItems.forEach((item, index) => {
      const xPos = margin + (index * 38);
      doc.setFillColor(...item.color);
      doc.circle(xPos, yPosition, 3, 'F');
      doc.setTextColor(...colors.gray);
      doc.setFont(undefined, 'normal');
      doc.text(item.label, xPos + 6, yPosition + 1);
    });
    
    yPosition += 20;
    drawGridVisualization();
  }
  
  function drawGridVisualization() {
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...colors.darkGray);
    
    const gridSize = parseInt((scanData.grid_size && scanData.grid_size.split('x')[0]) || 3);
    doc.text(`Average Rankings Across ${gridSize}×${gridSize} Grid (${gridSize * gridSize} Locations)`, 
      pageWidth/2, yPosition, { align: 'center' });
    
    yPosition += 20;
    
    // Larger grid cells for better visibility
    const cellSize = 22;
    const gridStartX = pageWidth / 2 - (gridSize * cellSize) / 2;
    
    if (scanData.results && scanData.results.length > 0) {
      const gridData = {};
      
      scanData.results.forEach(result => {
        const key = `${result.latitude},${result.longitude}`;
        if (!gridData[key]) {
          gridData[key] = { totalRank: 0, count: 0 };
        }
        if (result.rank > 0) {
          gridData[key].totalRank += result.rank;
          gridData[key].count++;
        }
      });
      
      const gridPoints = Object.entries(gridData).map(([key, data]) => {
        const [lat, lng] = key.split(',');
        const avgRank = data.count > 0 ? data.totalRank / data.count : 0;
        return { lat: parseFloat(lat), lng: parseFloat(lng), avgRank };
      });
      
      // Draw grid points
      for (let row = 0; row < gridSize; row++) {
        for (let col = 0; col < gridSize; col++) {
          const index = row * gridSize + col;
          const point = gridPoints[index];
          const xPos = gridStartX + col * cellSize;
          const yPos = yPosition + row * cellSize;
          
          if (point) {
            let fillColor;
            let textColor = colors.white;
            
            if (point.avgRank === 0) {
              fillColor = [200, 200, 200];
              textColor = colors.darkGray;
            } else if (point.avgRank <= 3) {
              fillColor = colors.secondary;
            } else if (point.avgRank <= 7) {
              fillColor = colors.purple;
            } else if (point.avgRank <= 15) {
              fillColor = colors.warning;
            } else {
              fillColor = colors.danger;
            }
            
            // Draw circle marker
            doc.setFillColor(...fillColor);
            doc.circle(xPos, yPos, 9, 'F');
            
            // Rank text
            doc.setFontSize(13);
            doc.setTextColor(...textColor);
            doc.setFont(undefined, 'bold');
            const rankText = point.avgRank > 0 ? `#${Math.round(point.avgRank)}` : 'NR';
            doc.text(rankText, xPos, yPos + 1.5, { align: 'center' });
          }
        }
      }
      yPosition += (gridSize * cellSize) + 15;
    }
    
    // Grid info
    doc.setFontSize(9);
    doc.setTextColor(...colors.gray);
    doc.setFont(undefined, 'normal');
    doc.text(`Grid Center: ${scanData.address || 'Location'}`, margin, yPosition);
    doc.text(`Coverage Radius: ${scanData.radius || '2km'} per grid point`, pageWidth/2, yPosition, { align: 'center' });
    doc.text(`Total Coverage Area: ~${gridSize * gridSize} km²`, pageWidth - margin, yPosition, { align: 'right' });
  }
  
  // GEOGRAPHIC PERFORMANCE INSIGHTS
  if (yPosition + 80 > pageHeight - margin) {
    doc.addPage();
    yPosition = margin;
  } else {
    yPosition += 25;
  }
  
  // doc.setFontSize(13);
  // doc.setFont(undefined, 'bold');
  // doc.setTextColor(...colors.darkGray);
  // doc.text('Geographic Performance Insights', margin, yPosition);
  
  // yPosition += 15;
  
  // // Best Performing Areas with green left border
  // doc.setFillColor(...colors.secondary);
  // doc.rect(margin, yPosition - 3, .5, 30, 'F');  // Thinner left border
  
  // // Trophy icon
  // doc.setFillColor(...colors.warning);
  // doc.setDrawColor(...colors.warning);
  // doc.setLineWidth(1.5);
  // doc.rect(margin + 8, yPosition - 1, 4, 3);
  // doc.line(margin + 8.5, yPosition + 2, margin + 8.5, yPosition + 4);
  // doc.line(margin + 11.5, yPosition + 2, margin + 11.5, yPosition + 4);
  // doc.rect(margin + 7, yPosition + 4, 6, 0.5, 'F');
  
  // doc.setFontSize(11);
  // doc.setFont(undefined, 'bold');
  // doc.setTextColor(...colors.darkGray);
  // doc.text('Best Performing Areas', margin + 18, yPosition + 1);
  
  // yPosition += 8;
  // doc.setFontSize(10);
  // doc.setFont(undefined, 'normal');
  // doc.setTextColor(...colors.gray);
  
  // const bestAreas = [
  //   'Downtown Core: Avg #2.8',
  //   'Business District: Avg #3.1',
  //   'Shopping Center: Avg #3.4'
  // ];
  
  // bestAreas.forEach(area => {
  //   doc.text(`${area}`, margin + 8, yPosition);
  //   yPosition += 5;
  // });
  
  // // Areas Needing Attention with red left border
  // yPosition += 8;
  // doc.setFillColor(...colors.danger);
  // doc.rect(margin, yPosition - 3, .5, 30, 'F');  // Thinner left border
  
  // // Warning triangle icon
  // doc.setFillColor(...colors.danger);
  // doc.setDrawColor(...colors.danger);
  // doc.setLineWidth(1.5);
  // doc.triangle(margin + 10, yPosition - 2, margin + 7, yPosition + 2, margin + 13, yPosition + 2, 'FD');
  // doc.setFillColor(...colors.white);
  // doc.setFontSize(10);
  // doc.text('!', margin + 10, yPosition + 1);
  
  // doc.setFontSize(11);
  // doc.setFont(undefined, 'bold');
  // doc.setTextColor(...colors.darkGray);
  // doc.text('Areas Needing Attention', margin + 18, yPosition + 1);
  
  // yPosition += 8;
  // doc.setFontSize(10);
  // doc.setFont(undefined, 'normal');
  // doc.setTextColor(...colors.gray);
  
  // const weakAreas = [
  //   'Suburban North: Avg #15.2',
  //   'Industrial Zone: Avg #18.1',
  //   'Residential West: Avg #12.7'
  // ];
  
  // weakAreas.forEach(area => {
  //   doc.text(`${area}`, margin + 8, yPosition);
  //   yPosition += 5;
  // });
  
  // // Strategic Recommendations with orange left border
  // yPosition += 8;
  // doc.setFillColor(...colors.warning);
  // doc.rect(margin, yPosition - 3, 1.5, 40, 'F');  // Thinner left border
  
  // // Lightbulb icon
  // doc.setFillColor(...colors.warning);
  // doc.circle(margin + 10, yPosition, 3);
  // doc.setDrawColor(...colors.warning);
  // doc.setLineWidth(0.5);
  // doc.line(margin + 8, yPosition + 3, margin + 12, yPosition + 3);
  // doc.line(margin + 8, yPosition + 4, margin + 12, yPosition + 4);
  // doc.line(margin + 9, yPosition + 5, margin + 11, yPosition + 5);
  
  // doc.setFontSize(11);
  // doc.setFont(undefined, 'bold');
  // doc.setTextColor(...colors.darkGray);
  // doc.text('Strategic Recommendations', margin + 18, yPosition + 1);
  
  // yPosition += 8;
  // doc.setFontSize(10);
  // doc.setFont(undefined, 'normal');
  // doc.setTextColor(...colors.gray);
  
  // const recommendations = [
  //   'Optimize underperforming keywords by enhancing local content relevance',
  //   'Expand presence in areas with low grid coverage',
  //   'Leverage top-performing keywords in marketing campaigns',
  //   'Monitor competitor movements in high-value locations',
  //   'Focus on improving positions for keywords ranking 4-10'
  // ];
  
  // recommendations.forEach(rec => {
  //   doc.text(`• ${rec}`, margin + 8, yPosition);
  //   yPosition += 5;
  // });
  
  // FOOTER
  const footerY = pageHeight - 15;
  
  // Footer line
  doc.setDrawColor(...colors.borderGray);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
  
  doc.setFontSize(9);
  doc.setTextColor(...colors.gray);
  doc.setFont(undefined, 'normal');
  doc.text('This report was generated by GridRank Local GRID Rank Tracker', pageWidth/2, footerY, { align: 'center' });
  
  // Footer links
  doc.setFontSize(9);
  doc.setTextColor(...colors.purple);
  doc.text('gridrank.com', pageWidth/2 - 40, footerY + 5);
  doc.setTextColor(...colors.gray);
  doc.text(' | ', pageWidth/2, footerY + 5, { align: 'center' });
  doc.setTextColor(...colors.purple);
  doc.text('support@gridrank.com', pageWidth/2 + 5, footerY + 5);
  
  return doc;
};